/**
 * Экран БДР v2: сетка «статья × колонка» в духе «БДР сводной» — разделы
 * жирным, строки с отступом, «нет данных» и ручные правки видны сразу.
 * Ширина реагирует на контейнер (`bdr`), а не на окно.
 */
import styled from 'styled-components';
import { fleetLeading, fleetType } from '@entities/fleet';
import { riseIn } from '@shared/theme/animations';
import { Select } from '@shared/ui/Input';

export const Root = styled.div`
  container: bdr / inline-size;
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

export const Lead = styled.p`
  max-width: 880px;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};

  & > .spacer {
    flex: 1 1 auto;
  }

  & > input[type='month'] {
    width: 170px;
  }

  & > select {
    width: auto;
    min-width: 200px;
  }
`;

/** Месяцы года: статус каждого периода плашкой, выбранный — рамкой. */
export const MonthStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const MonthChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 92px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}`};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.small};
  text-align: left;
  cursor: pointer;

  & .m {
    font-weight: 600;
  }

  & .s {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(5)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;

  & b {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const TableBox = styled.div`
  overflow: auto;
  max-height: 70vh;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

export const Grid = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  font-variant-numeric: tabular-nums;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    max-width: 150px;
    vertical-align: bottom;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: right;
    white-space: normal;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  th:first-child,
  td:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: 280px;
    max-width: 360px;
    text-align: left;
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  thead th:first-child {
    z-index: 3;
  }

  td {
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    text-align: right;
    white-space: nowrap;
  }

  tr.total > td {
    font-weight: 600;
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.total > td:first-child {
    background: ${({ theme }) => theme.colors.surface};
  }

  td.name {
    white-space: normal;
  }

  td.name .row {
    display: inline-flex;
    align-items: baseline;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }

  td.name .no {
    color: ${({ theme }) => theme.colors.text.muted};
    font-size: ${fleetType.micro};
  }

  td.cell {
    cursor: pointer;
  }

  td.cell:hover,
  td.cell:focus-within {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  td.nodata {
    color: ${({ theme }) => theme.colors.status.warning};
    font-size: ${fleetType.small};
  }

  td.manual {
    box-shadow: inset 3px 0 0 ${({ theme }) => theme.colors.primary};
  }

  td.totalcol {
    font-weight: 600;
  }
`;

export const CellButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    border-radius: ${({ theme }) => theme.radius.xs};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const Toggle = styled.button`
  width: 18px;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  cursor: pointer;
`;

export const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > h3 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.base};
    font-weight: 600;
  }
`;

export const AnomalyList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};

  & > li {
    padding: ${({ theme }) => theme.spacing(3)};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.radius.md};
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-size: ${fleetType.body};
  }

  & .head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    font-weight: 600;
  }

  & .desc {
    margin-top: ${({ theme }) => theme.spacing(1)};
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: ${fleetLeading.normal};
  }

  & .actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

/** Цепочка трассировки строки: шаги сверху вниз. */
export const Chain = styled.ol`
  margin: 0;
  padding: 0 0 0 ${({ theme }) => theme.spacing(5)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  & > li {
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }

  & pre {
    margin: ${({ theme }) => theme.spacing(1)} 0 0;
    padding: ${({ theme }) => theme.spacing(2)};
    max-height: 220px;
    overflow: auto;
    border-radius: ${({ theme }) => theme.radius.sm};
    background: ${({ theme }) => theme.colors.surface};
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.micro};
    white-space: pre-wrap;
  }
`;

export const DocsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fleetType.small};
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    text-align: left;
  }

  td.num,
  th.num {
    text-align: right;
    white-space: nowrap;
  }
`;

/** Название статьи с отступом по глубине в дереве шаблона. */
export const NameCell = styled.td<{ $depth: number }>`
  && {
    padding-left: ${({ theme, $depth }) => `calc(${theme.spacing(3)} + ${$depth * 18}px)`};
  }
`;

/** Выбор области: шириной с подпись, а не во всю строку. */
export const ScopeSelect = styled(Select)`
  width: auto;
  min-width: 240px;
  max-width: 420px;
`;
